<script setup lang="ts">
import CompanyDetailBody from "@/components/models/company/detail/CompanyDetailBody.vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { definePage } from "unplugin-vue-router/runtime";
import { useAbility } from "@/api/models/auth/ability";
import OrganizationSectionHero from "@/components/shared/headers/OrganizationSectionHero.vue";
import Breadcrumbs from "@/components/shared/Breadcrumbs.vue";
import { ORGANIZATION_ACCENTS } from "@/components/shared/navbar/useNavbarSubItems";
import { useCompanyDetail } from "@/composables/models/company/useCompanyMe";
import { useCompanyHeroStats } from "@/composables/models/company/useCompanyHeroStats";
import {
  ORGANIZATION_LEVEL_PATHS,
  useMyOrganization,
} from "@/composables/shared/useMyOrganization";

definePage({
  meta: {
    title: "generic.action.viewEntity",
    titleParams: { entity: "generic.entity.company" },
    checkPrivileges: () => {
      const { can } = useAbility();
      return (
        can($permActions.READ, $permSubjects.PRACTICE__COMPANY) ||
        can($permActions.COMPANY_ME_VIEW, $permSubjects.PRACTICE__COMPANY)
      );
    },
  },
});

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const companyId = computed(() => (route.params as { id: string }).id);
const useMe = computed(() => route.query.scope === "me");
const { data: company, isLoading: loadingCompany } = useCompanyDetail(
  { id: companyId },
  { useMe, redirectOnNotFound: true }
);

const { msos } = useMyOrganization();

const ownerMso = computed(() => (msos.value.length === 1 ? msos.value[0] : undefined));
const ownerName = computed(() => ownerMso.value?.name);

const { can } = useAbility();

const canEdit = computed(
  () =>
    !!company.value &&
    (can(
      $permActions.UPDATE,
      $permSubjects.PRACTICE__COMPANY,
      company.value.user_permissions,
      "any"
    ) ||
      can(
        $permActions.COMPANY_ME_EDIT,
        $permSubjects.PRACTICE__COMPANY,
        company.value.user_permissions,
        "any"
      ))
);

const handleEdit = () => {
  if (!company.value) return;

  router.push({
    name: "/companies/[id].edit",
    params: { id: company.value.id },
    query: route.query.scope === "me" ? { scope: "me" } : undefined,
  });
};

const breadcrumbItems = computed(() => [
  {
    label: ownerName.value ?? t("generic.entity.company", 2),
    to: ORGANIZATION_LEVEL_PATHS.practices,
  },
  { label: company.value?.name ?? "" },
]);

const heroStats = useCompanyHeroStats(company);
</script>

<template>
  <v-skeleton-loader
    :loading="loadingCompany"
    type="heading,card"
  >
    <template v-if="company">
      <breadcrumbs :items="breadcrumbItems" />

      <organization-section-hero
        :company="company"
        :eyebrow="t('generic.entity.company', 1)"
        :title="company.name"
        :accent-color="ORGANIZATION_ACCENTS.practices"
        icon="mdi-office-building-outline"
        :stats="heroStats"
        :show-edit="canEdit"
        edit-test-id="company-detail-edit-button"
        @edit="handleEdit"
      />

      <company-detail-body :company="company" />
    </template>
  </v-skeleton-loader>
</template>
